import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'utils/axios';
import { debounce } from 'utils/global-functions';

import {
    Box,
    Button,
    IconButton,
    Divider,
    Stack,
    Skeleton,
    Autocomplete,
    TextField,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Typography
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { useTheme } from '@mui/material/styles';

const AddProviderSidebar = ({ onClose, openProviderDetailsSidebar, schoolYears, currentSchoolYearId }) => {
    const [contacts, setContacts] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const fieldsStackRef = useRef(null);
    const addButtonRef = useRef(null);

    const theme = useTheme();

    const initialValues = {
        provider: {
            id: '',
            name: ''
        },
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        phone: '',
        notes: '',
        schoolYearId: ''
    };

    const validationSchema = Yup.object().shape({
        provider: Yup.object().shape({
            name: Yup.string()
                .required('Provider Name is required')
                .test('first-and-last-name', 'Please enter both first and last name', (value) => {
                    if (!value) return false;
                    return value.trim().split(' ').length > 1;
                })
        }),
        email: Yup.string().email('Invalid email').required('Email is required'),
        phone: Yup.string().required('Phone is required')
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async () => {
            setErrorMessage('');

            const submitValues = formik.values;
            submitValues.name = formik.values.provider?.name;
            if (formik.values.provider?.id) {
                submitValues.id = formik.values.provider.id;
            }

            try {
                const { data } = await axios.post('/api/providerintake/add', submitValues);
                openProviderDetailsSidebar(data, formik.values.schoolYearId);
                onClose();
            } catch (error) {
                setErrorMessage(error);
                addButtonRef.current?.scrollIntoView();
            }
        }
    });

    const { values, setFieldValue, errors, touched, setFieldTouched, handleSubmit, isSubmitting, validateField, handleBlur } = formik;

    const debouncedValidateField = useMemo(() => debounce(validateField, 150), [validateField]);

    const debouncedFetchContacts = useMemo(
        () =>
            debounce(async (searchText) => {
                if (searchText) {
                    try {
                        const { data } = await axios.get(`/api/providerintake/searchcontacts?searchText=${searchText}`);
                        setContacts(data);
                    } catch (error) {
                        console.error('Error fetching contacts:', error);
                    }
                } else {
                    setContacts([]);
                }
            }, 500),
        []
    );

    useEffect(() => {
        setFieldValue('schoolYearId', currentSchoolYearId || '');
    }, [currentSchoolYearId]);

    useEffect(() => {
        debouncedFetchContacts(values.provider?.name);
        return () => debouncedFetchContacts.cancel && debouncedFetchContacts.cancel();
    }, [values.provider?.name]);

    useEffect(() => {
        const onProviderChange = async () => {
            const provider = values.provider?.id ? contacts.find((c) => c.id === values.provider.id) : null;

            const setFieldFromProvider = (field) => setFieldValue(field, (provider && provider[field]) || '');

            setFieldFromProvider('addressLine1');
            setFieldFromProvider('addressLine2');
            setFieldFromProvider('city');
            setFieldFromProvider('state');
            setFieldFromProvider('zip');
            setFieldFromProvider('email');
            setFieldFromProvider('phone');
            debouncedValidateField('email');
        };

        onProviderChange();
    }, [values.provider?.id]);

    return (
        <FormikProvider value={formik}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '95vh', paddingBottom: 7.8 }}>
                <div style={{ marginRight: '15px', marginLeft: '15px', marginTop: '10px' }}>
                    <div>
                        <h4>
                            Add Provider
                            <IconButton
                                style={{
                                    float: 'right',
                                    padding: '0px 0px 0px 0px',
                                    marginTop: '-5px'
                                }}
                                onClick={onClose}
                            >
                                <CancelIcon />
                            </IconButton>
                        </h4>
                    </div>
                    <Divider />
                </div>
                <Stack spacing={1.5} style={{ marginTop: '1.25vw', marginRight: '10px', marginLeft: '10px' }} ref={fieldsStackRef}>
                    {!schoolYears?.length ? (
                        Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} variant="rounded" height={50} />)
                    ) : (
                        <>
                            <Autocomplete
                                options={contacts.map((c) => ({ label: c.name, value: c.id, email: c.email })) || []}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.value}>
                                        <Stack>
                                            <Typography variant="subtitle1">{option.label}</Typography>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                {option.email}
                                            </Typography>
                                        </Stack>
                                    </li>
                                )}
                                filterOptions={(x) => x}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Provider Name"
                                        variant="outlined"
                                        error={touched.provider && Boolean(errors.provider)}
                                        helperText={
                                            (touched.provider && errors.provider?.name) || "Name entered MUST be the provider's legal name"
                                        }
                                        onChange={(e) => setFieldValue('provider', { name: e.target.value || '', id: '' })}
                                    />
                                )}
                                onChange={(event, newValue) => {
                                    setFieldValue('provider', { name: newValue?.label || '', id: newValue?.value || '' });
                                }}
                                value={values.provider?.name || ''}
                                onBlur={() => setFieldTouched('provider')}
                                freeSolo
                                disableClearable={!values.provider?.id}
                            />
                            <TextField
                                label="Email"
                                disabled={Boolean(values.provider?.id)}
                                variant="outlined"
                                fullWidth
                                value={values.email}
                                onChange={(e) => setFieldValue('email', e.target.value)}
                                onBlur={handleBlur}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                                name="email"
                            />
                            <TextField
                                label="Phone"
                                disabled={Boolean(values.provider?.id)}
                                variant="outlined"
                                fullWidth
                                value={values.phone}
                                onChange={(e) => setFieldValue('phone', e.target.value)}
                                onBlur={handleBlur}
                                error={touched.phone && Boolean(errors.phone)}
                                helperText={touched.phone && errors.phone}
                                name="phone"
                            />
                            <TextField
                                label="Address Line 1"
                                disabled={Boolean(values.provider?.id)}
                                variant="outlined"
                                fullWidth
                                value={values.addressLine1}
                                onChange={(e) => setFieldValue('addressLine1', e.target.value)}
                                name="addressLine1"
                            />
                            <TextField
                                label="Address Line 2"
                                disabled={Boolean(values.provider?.id)}
                                variant="outlined"
                                fullWidth
                                value={values.addressLine2}
                                onChange={(e) => setFieldValue('addressLine2', e.target.value)}
                                name="addressLine2"
                            />
                            <TextField
                                label="City"
                                disabled={Boolean(values.provider?.id)}
                                variant="outlined"
                                fullWidth
                                value={values.city}
                                onChange={(e) => setFieldValue('city', e.target.value)}
                                name="city"
                            />
                            <TextField
                                label="State"
                                disabled={Boolean(values.provider?.id)}
                                variant="outlined"
                                fullWidth
                                value={values.state}
                                onChange={(e) => setFieldValue('state', e.target.value)}
                                name="state"
                            />
                            <TextField
                                label="Zip"
                                disabled={Boolean(values.provider?.id)}
                                variant="outlined"
                                fullWidth
                                value={values.zip}
                                onChange={(e) => setFieldValue('zip', e.target.value)}
                                name="zip"
                            />
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="school-year-label">School Year</InputLabel>
                                <Select
                                    labelId="school-year-label"
                                    label="School Year"
                                    value={values.schoolYearId || ''}
                                    onChange={(e) => setFieldValue('schoolYearId', e.target.value)}
                                >
                                    {schoolYears?.map((sy) => (
                                        <MenuItem key={sy.id} value={sy.id}>
                                            {sy.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Notes"
                                variant="outlined"
                                fullWidth
                                value={values.notes}
                                onChange={(e) => setFieldValue('notes', e.target.value)}
                                multiline
                                rows={4}
                                name="notes"
                            />
                            {errorMessage && (
                                <Alert severity="error" onClose={() => setErrorMessage('')}>
                                    {errorMessage}
                                </Alert>
                            )}
                        </>
                    )}
                </Stack>
                <span ref={addButtonRef} />
                <div
                    style={{
                        paddingBottom: '18px',
                        paddingTop: '18px',
                        paddingRight: '16px',
                        position: 'fixed',
                        bottom: '0px',
                        backgroundColor: 'white',
                        zIndex: 1
                    }}
                >
                    <div
                        style={{
                            width: fieldsStackRef.current?.getBoundingClientRect().width,
                            marginLeft: '10px',
                            marginRight: '10px'
                        }}
                    >
                        <Button variant="contained" size="large" disabled={isSubmitting} fullWidth onClick={handleSubmit}>
                            {isSubmitting && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px'
                                    }}
                                />
                            )}
                            Add
                        </Button>
                    </div>
                </div>
            </Box>
        </FormikProvider>
    );
};

export default AddProviderSidebar;
